import { useSelector } from 'react-redux';
import type { ReportCategories } from '../../../../common/enums/reportCategories';
import type { ServiceCode } from '../../../../common/enums/serviceCode';
import type { IReduxState } from '../../../configureStore';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { ServiceType } from '../../../../common/enums/serviceType';
import { ButtonContainer } from '../common';
import { AcceptAndStartProcedureOrConsultButton } from './AcceptAndStartProcedureOrConsultButton';
import { AddConsultReportButton } from './AddConsultReportButton';
import { AddProcedureButton } from './AddProcedureButton';
import { AssignProcessToColleagueButton } from './AssignProcessToColleagueButton';
import { BecomeRequesterButton } from './BecomeRequesterButton';
import { CancelProcessButton } from './CancelProcessButton';
import { EditRequestButton } from './EditRequestButton';
import { PrintPdfButton } from './PrintPdfButton';
import { TakeRequestButton } from './TakeRequestButton';
import { TakeRequestFromOrganizationButton } from './TakeRequestFromOrganizationButton';

interface IRequestReadButtonProps {
    additionalButtons?: JSX.Element[];
    addProcedure?: () => Promise<void>;
    addReport?: (reportCategory: ReportCategories.consultReport) => Promise<void>;
    assignProcessColleague: () => Promise<void>;
    editRequest: () => Promise<void>;
    isLastItem: boolean;
    reloadProcess: () => Promise<void>;
    reloadRequest: () => Promise<void>;
    removeProcess: () => Promise<void>;
    request: IRequestGetOne;
    serviceCode: ServiceCode;
}

export function RequestReadButtons(props: IRequestReadButtonProps) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const additionalButtons = props.additionalButtons ?? [];

    if (isManager) {
        return null;
    }

    if (!props.isLastItem) {
        if (
            props.request.status === RequestStatus.draft ||
            props.request.txcsProcesId ||
            (props.request.service.serviceType !== ServiceType.referral &&
                props.request.service.serviceType !== ServiceType.referralSmall &&
                props.request.serviceType !== ServiceType.examination)
        ) {
            return null;
        }

        return (
            <ButtonContainer
                alignRight
                buttons={[<PrintPdfButton request={props.request} />].concat(additionalButtons)}
            />
        );
    }

    return (
        <ButtonContainer
            alignRight
            buttons={getRequestReadButtons(props).concat(additionalButtons)}
        />
    );
}

export function getRequestReadButtons(props: IRequestReadButtonProps) {
    return [
        <CancelProcessButton
            cancelProcess={props.removeProcess}
            request={props.request}
            serviceCode={props.serviceCode}
        />,
        <PrintPdfButton request={props.request} />,
        <AssignProcessToColleagueButton
            addReport={props.addReport}
            assignProcessColleague={props.assignProcessColleague}
            request={props.request}
        />,
        <AcceptAndStartProcedureOrConsultButton
            addProcedure={props.addProcedure}
            addReport={props.addReport}
            reloadProcess={props.reloadProcess}
            request={props.request}
        />,
        <AddConsultReportButton
            addReport={props.addReport}
            request={props.request}
        />,
        <AddProcedureButton
            addProcedure={props.addProcedure}
            request={props.request}
        />,
        <EditRequestButton
            editRequest={props.editRequest}
            request={props.request}
        />,
        <TakeRequestButton
            reloadProcess={props.reloadProcess}
            request={props.request}
        />,
        <TakeRequestFromOrganizationButton
            addProcedure={props.addProcedure}
            addReport={props.addReport}
            reloadProcess={props.reloadProcess}
            request={props.request}
        />,
        <BecomeRequesterButton
            reloadProcess={props.reloadProcess}
            request={props.request}
        />,
    ];
}
